import Button from "app/storybookComponents/Button";
import { useEffect, useState } from "react";
import DeactivateSelfModal from "./Modals/DeactivateSelfModal";
import {
  selectCurrentUserAccountId,
  selectCurrentUserInfo,
  selectIsCurrentUserAdmin,
  updateUserById,
} from "app/containers/Global/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import DepartmentDropdown from "app/components/Dropdowns/DepartmentDropdown";
import DeactivateUserModal from "app/components/DeactivateUserModal";
import {
  selectCompanySettings,
  selectDepartments,
  selectIsDepartmentsHidden,
} from "app/containers/AdminConsole/slice";
import { TIMEZONE_OPTIONS } from "utils/constants";
import GenderDropdown from "app/components/Dropdowns/GenderDropdown";
import Loading from "app/storybookComponents/Loading";
import { useDebounceCallback } from "utils/hooks";
import TimezoneDropdown from "app/components/Dropdowns/TimezoneDropdown";
import EditUserEmailModal from "./Modals/EditUserEmailModal";
import EditUserJobTitleModal from "./Modals/EditUserJobTitleModal";
import EditUserNameModal from "./Modals/EditUserNameModal";
import ResetPasswordModal from "./ResetPasswordModal";
import {
  getUserAbsences,
  selectGetUserAbsencesStatus,
  selectUserAbsencesByUserAccountId,
  selectUpdateUserTimezoneStatus,
  updateUserTimezone,
} from "./slice";
import OnLeaveModal from "./OnLeaveModal";

const UserGeneralSettings = () => {
  const dispatch = useAppDispatch();
  const companySettings = useAppSelector(selectCompanySettings);
  const loggedInUserId = useAppSelector(selectCurrentUserAccountId);
  const userInfo = useAppSelector(selectCurrentUserInfo);
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const departments = useAppSelector(selectDepartments);
  const areDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const getUserAbsencesStatus = useAppSelector(selectGetUserAbsencesStatus);
  const userAbsences = useAppSelector(
    selectUserAbsencesByUserAccountId(loggedInUserId ?? 0)
  );
  const updateUserTimezoneStatus = useAppSelector(
    selectUpdateUserTimezoneStatus
  );
  const [showDeactivateUserModal, setShowDeactivateUserModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [editModalShowing, setEditModalShowing] = useState<string | null>(null);
  const [showOnLeaveModal, setShowOnLeaveModal] = useState(false);
  const [timezoneSelected, setTimezoneSelected] = useState<{
    label: string;
    tzCode: string;
  } | null>(null);

  useEffect(() => {
    if (loggedInUserId) {
      dispatch(getUserAbsences({ userAccountId: loggedInUserId }));
    }
  }, [dispatch, loggedInUserId]);

  useEffect(() => {
    if (userInfo?.timezone || companySettings?.timezone) {
      const foundTimezone = TIMEZONE_OPTIONS.find(
        (tz) => tz.tzCode === (userInfo?.timezone ?? companySettings?.timezone)
      );
      setTimezoneSelected(foundTimezone ?? null);
    }
  }, [userInfo, companySettings]);

  const onDepartmentSelect = useDebounceCallback(
    (departmentId: number | null) => {
      dispatch(
        updateUserById({
          userAccountId: loggedInUserId ?? 0,
          payload: { departmentId },
        })
      );
    },
    500
  );

  const onGenderSelect = useDebounceCallback((genderPayload: string) => {
    const genderLower = genderPayload.toLowerCase();
    const gender =
      genderLower === "not disclosed" ? "do-not-disclose" : genderLower;
    dispatch(
      updateUserById({
        userAccountId: loggedInUserId ?? 0,
        payload: { gender },
      })
    );
  }, 500);

  const getUserFullName = () => {
    const firstName = userInfo?.firstName ?? "";
    const lastName = userInfo?.lastName ?? "";
    return `${firstName} ${lastName}`.trim();
  };

  const getDepartmentSection = () => {
    if (areDepartmentsHidden) {
      return null;
    }
    const department = userInfo?.departmentId
      ? departments[userInfo?.departmentId]
      : null;
    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Department</h3>
          <p>{department?.name ?? "No department"}</p>
        </div>
        <div className="setting-row-action">
          <DepartmentDropdown
            onSelect={(departmentId) => {
              onDepartmentSelect(departmentId);
            }}
            selectedDepartmentId={userInfo?.departmentId ?? null}
          />
        </div>
      </div>
    );
  };

  const getDateString = (date?: string | Date | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString();
  };

  const getOnLeaveSection = () => {
    const currentAbsence =
      userAbsences?.activeAbsence ?? userAbsences?.futureAbsence;
    const isOnLeave = !!currentAbsence;
    const isLoading = getUserAbsencesStatus === "loading";
    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>On Leave</h3>
          {isOnLeave ? (
            <p>
              {getDateString(currentAbsence?.leaveStartDate)} -{" "}
              {getDateString(currentAbsence?.leaveEndDate)}
            </p>
          ) : null}
          {isLoading && !isOnLeave ? <Loading /> : null}
        </div>
        <div className="setting-row-action">
          <Button
            variant="secondary-blue"
            onClick={() => setShowOnLeaveModal(true)}
            disabled={isLoading}
          >
            {isOnLeave ? "Edit leave" : "Start leave"}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <ResetPasswordModal
        show={showResetPasswordModal}
        handleClose={() => setShowResetPasswordModal(false)}
      />
      {isAdmin ? (
        <DeactivateUserModal
          show={showDeactivateUserModal}
          onHide={() => setShowDeactivateUserModal(false)}
          userAccountId={loggedInUserId}
        />
      ) : (
        <DeactivateSelfModal
          show={showDeactivateUserModal}
          onHide={() => setShowDeactivateUserModal(false)}
        />
      )}
      <EditUserEmailModal
        isOpen={editModalShowing === "email"}
        onClose={() => setEditModalShowing(null)}
        userAccountId={loggedInUserId}
      />
      <EditUserJobTitleModal
        isOpen={editModalShowing === "jobTitle"}
        onClose={() => setEditModalShowing(null)}
        userAccountId={loggedInUserId}
      />
      <EditUserNameModal
        isOpen={editModalShowing === "name"}
        onClose={() => setEditModalShowing(null)}
        userAccountId={loggedInUserId}
      />
      <OnLeaveModal
        show={showOnLeaveModal}
        onClose={() => setShowOnLeaveModal(false)}
        onConfirm={() => {
          setShowOnLeaveModal(false);
        }}
      />
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Full Name</h3>
          <p>{getUserFullName()}</p>
        </div>
        <div className="setting-row-action">
          <Button
            variant="secondary-blue"
            onClick={() => setEditModalShowing("name")}
          >
            Edit name
          </Button>
        </div>
      </div>
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Job Title</h3>
          <p>{userInfo?.jobTitle ?? "No job title"}</p>
        </div>
        <div className="setting-row-action">
          <Button
            variant="secondary-blue"
            onClick={() => setEditModalShowing("jobTitle")}
          >
            Edit job title
          </Button>
        </div>
      </div>
      {getDepartmentSection()}
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Gender</h3>
          <p className="text-capitalize">
            {userInfo?.gender ?? "No gender specified"}
          </p>
        </div>
        <div className="setting-row-action">
          <GenderDropdown
            selectedGender={
              userInfo?.gender === "do-not-disclose"
                ? "Not Disclosed"
                : userInfo?.gender
            }
            onGenderSelect={(gender) => {
              onGenderSelect(gender);
            }}
            toggleStyles={{
              textTransform: "capitalize",
            }}
          />
        </div>
      </div>
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Time zone</h3>
          <p>{timezoneSelected?.label ?? null}</p>
        </div>
        <div className="setting-row-action">
          <TimezoneDropdown
            selectedValue={timezoneSelected?.label}
            onTzCodeSelect={(tzCode) => dispatch(updateUserTimezone(tzCode))}
            disabled={updateUserTimezoneStatus === "loading"}
          />
        </div>
      </div>
      <hr className="m-0" />
      {getOnLeaveSection()}
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Password</h3>
          <p>******************</p>
        </div>
        <div className="setting-row-action">
          <Button
            variant="secondary-blue"
            onClick={() => setShowResetPasswordModal(true)}
          >
            Change password
          </Button>
        </div>
      </div>
      <div className="column-gap-10px">
        <h3>Deactivate Account</h3>
        <p>
          Deactivating your account will remove your access to the platform and
          delete your team memberships.
        </p>
        <div>
          <Button
            variant="secondary-danger"
            onClick={() => {
              setShowDeactivateUserModal(true);
            }}
          >
            Deactivate account
          </Button>
        </div>
      </div>
    </>
  );
};

export default UserGeneralSettings;
